import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Page from "@insite/mobius/Page";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { setGA4Event } from "@solveindustrial/Store/Handlers/Utilities/CustomTracking";
import debounce from "lodash/debounce";
import * as React from "react";
import { ReactNode, useEffect } from "react";
import styled, { css } from "styled-components";

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    showSignInOverlay,
    signInOverlay,
    showStorePickupOverlay,
    storePickupOverlay,
    showAddressBookOverlay,
    addressBookOverlay,
}: PageLayoutProps) => {
    /* Verndale Custom Feature - Set CSS Width variable for global usage - START */
    const [cssVariables, setCssVariables] = React.useState(
        css`
            --width: 100vw;
        `,
    );

    const handleSetCssVariables = () => {
        const width = document?.documentElement?.clientWidth;
        setCssVariables(css`
            --width: ${width}px;
        `);
    };

    useEffect(() => {
        handleSetCssVariables();
        const debouncedHandleResize = debounce(handleSetCssVariables, 500);
        window.addEventListener("resize", debouncedHandleResize);
    }, []);

    useEffect(() => {
        // Check for pending GA4 event
        const pendingSignIn = sessionStorage.getItem("pendingSignInGA4");
        const signInUserId = sessionStorage.getItem("signInUserId");

        if (pendingSignIn === "true") {
            // Create an observer instance
            const observer = new MutationObserver((mutations, obs) => {
                // Look for a warehouse-menu-label element that indicates page is ready
                const pageIsReady = document.querySelector("[data-test-selector='headerSignOutLink']");

                if (pageIsReady) {
                    console.log("Page is fully loaded, setting GA4 event");
                    setGA4Event([], "login", {
                        user_id: signInUserId || "",
                    });
                    sessionStorage.setItem("pendingSignInGA4", "false");
                    sessionStorage.setItem("signInUserId", "");

                    // Disconnect the observer
                    obs.disconnect();
                }
            });

            // Start observing
            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });

            // Cleanup
            return () => observer.disconnect();
        }
    }, []);
    /* Verndale Custom Feature - END */

    return (
        /* Verndale Custom Feature - Set  */
        <PageLayoutWrapper css={cssVariables}>
            {showHeader && header}
            {showCompactHeader && compactHeader}
            {showBreadcrumbs && (
                <Page as="div">
                    <PageBreadcrumbs />
                </Page>
            )}
            {pageContent}
            {showFooter && footer}
            {showSignInOverlay && signInOverlay}
            {showStorePickupOverlay && storePickupOverlay}
            {showAddressBookOverlay && addressBookOverlay}
        </PageLayoutWrapper>
    );
};

/* Verndale Custom Feature - Allow injectable CSS for the page layout wrapper. */
const PageLayoutWrapper = styled.div<InjectableCss>`
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    > main {
        flex: 1 1 0%;
    }

    @media print {
        display: block;
    }

    ${injectCss}
`;

export default PageLayout;
